import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import { baseURL } from '@functions/api'
import { mapCampaignInspectionToState } from '@functions/campaignInspection'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as campaignInspectionActions from '@redux/modules/campaignInspection'

import defaultFormState, { requiredFields } from '@models/campaignInspection'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { CampaignInspectionModel, CampaignInspectionRequestOptions } from '@models/types'

type GenerateUrlsOptions = {
  prefixRoute?: string,
}

export const generateUrls = (campaignInspection: Partial<CampaignInspectionModel>, options?: GenerateUrlsOptions) => {
  const { id } = campaignInspection || {}
  const { prefixRoute } = options || {}

  return {
    campaignInspectionsOverviewUrl: `${prefixRoute}/inspections`,
    iCalDownload: `${baseURL}/campaign_inspections/${id}/export_ics`,
  }
}

type CreateCampaignInspectionParams = {
  campaignInspectionParams: Partial<CampaignInspectionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignInspectionRequestOptions,
}

const createCampaignInspection = (params: CreateCampaignInspectionParams) => {
  const { dispatch, campaignInspectionParams, requestOptions } = params
  const { createCampaignInspection: createFn } = campaignInspectionActions

  return dispatch(createFn(campaignInspectionParams, requestOptions))
}

type UpdateCampaignInspectionParams = {
  campaignInspection: CampaignInspectionModel,
  campaignInspectionParams: Partial<CampaignInspectionModel>,
  dispatch: AppDispatch,
  requestOptions?: CampaignInspectionRequestOptions,
}

const updateCampaignInspection = (params: UpdateCampaignInspectionParams) => {
  const {
    dispatch, campaignInspection, campaignInspectionParams, requestOptions,
  } = params
  const { updateCampaignInspection: updateFn } = campaignInspectionActions

  const updatedParams = {
    id: campaignInspection.id,
    ...campaignInspectionParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteCampaignInspectionParams = {
  campaignInspection: DeleteParams<CampaignInspectionModel>,
  dispatch: AppDispatch,
}

const deleteCampaignInspection = (params: DeleteCampaignInspectionParams) => {
  const { dispatch, campaignInspection } = params
  const { deleteCampaignInspection: deleteFn } = campaignInspectionActions

  return dispatch(deleteFn(campaignInspection))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useCampaignInspectionForm(
  campaignInspection: Partial<CampaignInspectionModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const campaignInspectionForm = useForm(
    defaultFormState,
    {
      // @ts-expect-error TS2345
      entity: mapCampaignInspectionToState(campaignInspection),
      requiredFields: [...requiredFields, ...customRequiredFields],
      validateOn,
    },
    [campaignInspection.id, campaignInspection.cache_key],
  )

  return {
    ...campaignInspectionForm,
  }
}

export const useRelations = (campaignInspection: Partial<CampaignInspectionModel> = {}) => {
  const { campaign_id } = campaignInspection

  const { campaigns } = useSelector(reduxState => reduxState.entities)

  const campaign = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}

  return {
    campaign,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: CampaignInspectionRequestOptions,
}

function useCampaignInspection(initEntity: Partial<CampaignInspectionModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: campaignInspection }: { entity: CampaignInspectionModel } = useLatestEntity(initEntity, 'campaignInspections')
  const { id } = campaignInspection

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { campaign } = useRelations(campaignInspection)

  useReduxAction(
    'campaignInspections',
    'loadCampaignInspection',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.campaignInspections)

  const portal = digObject(campaign, 'data.portal', '')

  return {
    callbacks: {
      createCampaignInspection: (
        campaignInspectionParams: Partial<CampaignInspectionModel>,
        entityOptions?: CampaignInspectionRequestOptions,
      ) => (
        createCampaignInspection({ campaignInspectionParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditCampaignInspection: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditCampaignInspectionModal',
        payload: { campaignInspection },
      }),
      deleteCampaignInspection: () => deleteCampaignInspection({ dispatch, campaignInspection }),
      updateCampaignInspection: (
        campaignInspectionParams: Partial<CampaignInspectionModel>,
        entityOptions?: CampaignInspectionRequestOptions,
      ) => (
        updateCampaignInspection({
          campaignInspection, campaignInspectionParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    campaign,
    campaignInspection,
    campaignSyncedWithPortal: !!portal,
    creating,
    deleting,
    loading: loading && !loadedIds.includes(campaignInspection.id),
    updating,
    urls: generateUrls(campaignInspection),
  }
}

export default useCampaignInspection
